import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "./apiClient";

/**
 * Loadtest generator — `/admin/api/v1/tools/loadtest/**`. Ecobra-only (feature `testing.loadtest-generator`, jinde
 * HIDDEN). `/download` vrací ZIP bundle (JMeter plán + CSV + warmup).
 */

export interface TargetSystem {
  id: string;
  displayName: string;
}

export interface LoadtestScenario {
  id: string;
  displayName: string;
  description: string;
}

export function useLoadtestSystems() {
  return useQuery({
    queryKey: ["loadtest", "systems"],
    queryFn: () => apiFetch<TargetSystem[]>("/admin/api/v1/tools/loadtest/systems"),
  });
}

export function useLoadtestScenarios() {
  return useQuery({
    queryKey: ["loadtest", "scenarios"],
    queryFn: () => apiFetch<LoadtestScenario[]>("/admin/api/v1/tools/loadtest/scenarios"),
  });
}

/**
 * Auth-server typy, pro které je v tomto buildu dostupný warm-up provider
 * (přes eCobra SOAP). Prázdné pole = warm-up nelze spustit → vygenerovaný plán
 * nepůjde rozběhnout. Mirror legacy `warmupService.availableAuthServerTypes()`.
 */
export function useLoadtestWarmupAvailable() {
  return useQuery({
    queryKey: ["loadtest", "warmup-available"],
    queryFn: () => apiFetch<string[]>("/admin/api/v1/tools/loadtest/warmup-available"),
  });
}

export interface LoadtestRequest {
  targetSystem: string;
  scenario: string;
  warmupMode: string;
  identitySource: {
    type: "LIST" | "PATTERN";
    csv: string | null;
    pattern: {
      usernamePrefix: string;
      startIndex: number;
      endIndex: number;
      padWidth: number;
      phonePattern: string | null;
      password: string | null;
      paatProfileId: string | null;
    } | null;
  };
  load: { threads: number; rampUpSeconds: number; loops: number };
  target: { protocol: string; host: string; port: number; authServerType: string };
  warmup: {
    clientIdent: string | null;
    system: number | null;
    deviceOSType: string;
    reason: string | null;
    sendSms: boolean;
    setPhone: boolean;
  };
  embedApiKey: boolean;
  apiKey: string | null;
}

/** POST /download → stáhne ZIP bundle. Vrací {ok} nebo {ok:false, error}. */
export async function downloadLoadtestBundle(request: LoadtestRequest): Promise<{ ok: boolean; error?: string }> {
  const res = await fetch("/admin/api/v1/tools/loadtest/download", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });

  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    const text = await res.text().catch(() => null);
    if (text) {
      try {
        const j = JSON.parse(text) as { message?: string; error?: string };
        msg = j.message || j.error || text;
      } catch {
        msg = text;
      }
    }
    return { ok: false, error: msg };
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "etts-loadtest.zip";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return { ok: true };
}
